import { AlgoliaProductCardRenderer } from '@lyle/algolia-product-card'
import {
  algoliaProductAttributes,
  AlgoliaProductFetcher,
} from '@lyle/algolia-product-fetcher'
import {
  algoliaUserToken,
  escapeHtml,
  parseJson,
  trackAlgoliaProductClick,
} from '@lyle/algolia-product-utils'
import { getLocalisedRoute, translateWeglotSearchRequests } from '@lyle/weglot'

const ALGOLIA_QUERY_ENDPOINT = appId => {
  return `https://${appId}-dsn.algolia.net/1/indexes/*/queries`
}
const DESKTOP_BREAKPOINT = '(width >= 1024px)'
const SEARCH_DEBOUNCE_MS = 250
const SEARCH_PANEL_TRANSITION_MS = 500
const SEARCH_RULE_CONTEXT = 'search-modal'
const LINK_ATTRIBUTES = ['handle', 'objectID', 'query', 'title']
const SUGGESTION_LIMIT = 3
const SUGGESTION_FETCH_LIMIT = 8
const POPULAR_SEARCH_LIMIT = 6
// Past searches for a product code (e.g. "kn2110vc") are not useful prompts.
const SUGGESTION_CODE_PATTERN = /\d{3,}/

class LyleHeaderSearch extends HTMLElement {
  #abortController = null
  #cardRenderer = null
  #config = null
  #closeTimer = null
  #debounceTimer = null
  #empty = null
  #emptyTitle = null
  #fetcher = null
  #header = null
  #inputs = []
  #isOpen = false
  #mobileGroups = null
  #panel = null
  #primaryGroups = null
  #products = null
  #productsLabel = null
  #query = ''
  #requestId = 0
  #resizeListener = null
  #restoreMenuOnClose = false
  #status = null
  #swatchRequestId = 0
  #toggles = []
  #viewportWidth = 0
  #viewResults = null

  connectedCallback() {
    this.#config = this.#parseConfig()
    this.#header = this.closest('lyle-site-header')
    this.#panel = this.querySelector('[data-header-search-panel]')
    this.#primaryGroups = this.querySelector(
      '[data-header-search-primary-groups]'
    )
    this.#mobileGroups = this.querySelector(
      '[data-header-search-mobile-groups]'
    )
    this.#products = this.querySelector('[data-header-search-products]')
    this.#productsLabel = this.querySelector(
      '[data-header-search-products-label]'
    )
    this.#viewResults = this.querySelector('[data-header-search-view-results]')
    this.#status = this.querySelector('[data-header-search-status]')
    this.#empty = this.querySelector('[data-header-search-empty]')
    this.#emptyTitle = this.querySelector('[data-header-search-empty-title]')

    if (!this.#config || !this.#panel) {
      return
    }

    this.#abortController = new AbortController()
    this.#cardRenderer = new AlgoliaProductCardRenderer({
      config: this.#config,
      icons: window.icons || {},
      isMobile: () => !this.#isDesktop(),
    })
    this.#fetcher = new AlgoliaProductFetcher({
      config: this.#config,
      signal: this.#abortController.signal,
    })

    this.#bindControls()
    this.#bindEvents()
  }

  disconnectedCallback() {
    this.#abortController?.abort()
    document.removeEventListener('keydown', this.#handleKeydown)
    window.removeEventListener('resize', this.#resizeListener)
    this.#header?.removeEventListener(
      'site-header:menu-open',
      this.#handleMenuOpen
    )

    if (this.#debounceTimer) {
      clearTimeout(this.#debounceTimer)
    }
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer)
    }

    if (this.#panel) {
      this.#setOpenState(false)
    }
  }

  #bindControls() {
    const root = this.#header || document

    this.#toggles = [
      ...root.querySelectorAll(
        `[data-header-search-toggle][aria-controls="${this.#panel.id}"]`
      ),
    ]
    this.#inputs = [
      ...new Set([
        ...root.querySelectorAll('[data-header-search-input]'),
        ...this.querySelectorAll('[data-header-search-input]'),
      ]),
    ]

    this.#toggles.forEach(toggle => {
      toggle.addEventListener('click', this.#handleToggle, {
        signal: this.#abortController.signal,
      })
      toggle.addEventListener('mouseenter', this.#prepareSearch, {
        signal: this.#abortController.signal,
      })
      toggle.addEventListener('touchstart', this.#prepareSearch, {
        passive: true,
        signal: this.#abortController.signal,
      })
    })
    ;[
      ...new Set([
        ...root.querySelectorAll('[data-header-search-form]'),
        ...this.querySelectorAll('[data-header-search-form]'),
      ]),
    ].forEach(form => {
      form.addEventListener('submit', this.#handleSubmit, {
        signal: this.#abortController.signal,
      })
    })

    this.#inputs.forEach(input => {
      input.addEventListener('input', this.#handleInput, {
        signal: this.#abortController.signal,
      })
      input.addEventListener('focus', this.#handleInputFocus, {
        signal: this.#abortController.signal,
      })
    })
    ;[
      ...new Set([
        ...root.querySelectorAll('[data-header-search-clear]'),
        ...this.querySelectorAll('[data-header-search-clear]'),
      ]),
    ].forEach(clear => {
      clear.addEventListener('click', this.#handleClear, {
        signal: this.#abortController.signal,
      })
    })
  }

  #bindEvents() {
    this.addEventListener('click', this.#handleCardClick, {
      signal: this.#abortController.signal,
    })
    document.addEventListener('keydown', this.#handleKeydown)
    this.#header?.addEventListener(
      'site-header:menu-open',
      this.#handleMenuOpen
    )
    this.#viewportWidth = window.innerWidth
    this.#resizeListener = () => {
      // The on-screen keyboard resizes the viewport height only. Closing on
      // that would hide the panel as soon as the input receives focus.
      if (window.innerWidth === this.#viewportWidth) {
        return
      }

      this.#viewportWidth = window.innerWidth

      if (this.#isOpen) {
        this.#close({ restoreMenu: false })
      }
    }
    window.addEventListener('resize', this.#resizeListener, { passive: true })
  }

  #handleToggle = event => {
    const toggle = event.currentTarget
    const isMobileToggle = toggle.dataset.headerSearchView === 'mobile'

    if (this.#isOpen && isMobileToggle) {
      this.#close()
      return
    }

    if (this.#isOpen) {
      this.#focusDesktopInput()
      return
    }

    this.#query = ''
    this.#syncInputs('')
    this.#open({ focus: !isMobileToggle })
  }

  #prepareSearch = () => {
    if (this.#isOpen || this.#query) {
      return
    }

    this.#scheduleSearch()
  }

  #handleInputFocus = () => {
    if (!this.#isOpen) {
      this.#open({ focus: false })
    }
  }

  #handleInput = event => {
    this.#query = event.currentTarget.value || ''
    this.#syncInputs(this.#query, event.currentTarget)

    if (!this.#isOpen) {
      this.#open({ focus: false })
    }

    this.#scheduleSearch()
  }

  #handleSubmit = event => {
    event.preventDefault()

    const value = this.#query.trim()

    if (!value) {
      this.#open({ focus: true })
      return
    }

    window.location.href = this.#searchUrl(value)
  }

  #handleClear = event => {
    const view = event.currentTarget.closest('[data-header-search-form]')
      ?.dataset.headerSearchView
    const hadQuery = Boolean(this.#query.trim())

    this.#query = ''
    this.#syncInputs('')

    if (view === 'desktop' || !hadQuery) {
      this.#close()
      return
    }

    this.#open({ focus: true })
  }

  #handleKeydown = event => {
    if (event.key === 'Escape' && this.#isOpen) {
      this.#close()
    }
  }

  #handleMenuOpen = () => {
    this.#close({ restoreMenu: false })
  }

  #handleCardClick = event => {
    const productLink = event.target.closest('[data-product-link]')
    const swatch = event.target.closest('[data-card-swatch]')

    if (swatch) {
      event.preventDefault()
      this.#selectSwatch(swatch)
      return
    }

    if (productLink) {
      this.#rememberSelectedProduct(productLink)
      void trackAlgoliaProductClick(
        this.#config,
        this.#productIndex(),
        productLink
      )
    }
  }

  #rememberSelectedProduct(element) {
    const productId = element.closest('[data-product-card]')?.dataset.productId

    try {
      if (productId) {
        sessionStorage.setItem('selected_product_id', productId)
      }

      if (element.dataset.queryId) {
        sessionStorage.setItem('queryId', element.dataset.queryId)
      } else {
        sessionStorage.removeItem('queryId')
      }
    } catch {
      // Storage can be unavailable in strict privacy modes.
    }
  }

  #open({ focus }) {
    if (!this.#panel) {
      return
    }

    if (!this.#isOpen) {
      this.#restoreMenuOnClose =
        !this.#isDesktop() &&
        this.#header?.classList.contains('site-header--menu-open')
    }

    this.dispatchEvent(
      new CustomEvent('header-search:open', {
        bubbles: true,
      })
    )
    this.#setOpenState(true)
    this.#scheduleSearch(0)

    if (focus) {
      requestAnimationFrame(() => this.#focusActiveInput())
    }
  }

  #close = ({ restoreMenu = true } = {}) => {
    if (!this.#isOpen) {
      return
    }

    const shouldRestoreMenu =
      restoreMenu && this.#restoreMenuOnClose && !this.#isDesktop()
    this.#restoreMenuOnClose = false

    this.#query = ''
    this.#syncInputs('')
    this.#setOpenState(false)
    this.dispatchEvent(
      new CustomEvent('header-search:close', {
        bubbles: true,
        detail: { restoreMenu: shouldRestoreMenu },
      })
    )
  }

  #setOpenState(isOpen) {
    this.#isOpen = isOpen
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer)
      this.#closeTimer = null
    }

    if (isOpen) {
      this.#panel.hidden = false
      requestAnimationFrame(() => {
        if (this.#isOpen) {
          this.#panel.classList.remove('is-hidden')
        }
      })
    } else {
      this.#panel.classList.add('is-hidden')
      this.#closeTimer = setTimeout(() => {
        if (!this.#isOpen) {
          this.#panel.hidden = true
        }
        this.#closeTimer = null
      }, SEARCH_PANEL_TRANSITION_MS)
    }

    this.#panel.setAttribute('aria-hidden', String(!isOpen))
    this.#header?.classList.toggle('site-header--search-open', isOpen)
    document.body.classList.toggle('header-search-open', isOpen)

    this.#toggles.forEach(toggle => {
      toggle.setAttribute('aria-expanded', String(isOpen))
    })

    this.#inputs.forEach(input => {
      input
        .closest('[data-header-search-shell]')
        ?.classList.toggle('is-active', isOpen)
    })
    this.#syncClearButtons()
  }

  #scheduleSearch(delay = SEARCH_DEBOUNCE_MS) {
    if (this.#debounceTimer) {
      clearTimeout(this.#debounceTimer)
    }

    this.#debounceTimer = setTimeout(() => {
      this.#debounceTimer = null
      void this.#search()
    }, delay)
  }

  async #search() {
    const requestId = ++this.#requestId
    const query = this.#query.trim()

    if (!this.#hasAlgoliaConfig()) {
      this.#renderEmpty(query)
      return
    }

    this.#setLoading(true)

    try {
      const requests = await translateWeglotSearchRequests(
        this.#buildRequests(query)
      )
      const response = await fetch(
        ALGOLIA_QUERY_ENDPOINT(this.#config.algolia.appId),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Algolia-API-Key': this.#config.algolia.apiKey,
            'X-Algolia-Application-Id': this.#config.algolia.appId,
          },
          body: JSON.stringify({
            requests,
          }),
          signal: this.#abortController.signal,
        }
      )

      if (!response.ok) {
        throw new Error('Algolia request failed')
      }

      const payload = await response.json()

      if (requestId !== this.#requestId) {
        return
      }

      this.#renderResults(query, payload.results || [])
    } catch {
      if (requestId === this.#requestId) {
        this.#renderEmpty(query)
      }
    } finally {
      if (requestId === this.#requestId) {
        this.#setLoading(false)
      }
    }
  }

  #buildRequests(query) {
    const indexes = this.#config.algolia.indexes
    const requests = [
      {
        indexName: indexes.suggestions,
        params: this.#linkParams({
          query,
          hitsPerPage: query ? SUGGESTION_FETCH_LIMIT : POPULAR_SEARCH_LIMIT,
          attributesToRetrieve: ['objectID', 'query'],
        }).toString(),
      },
      {
        indexName: query ? indexes.products : indexes.trendingProducts,
        params: this.#productParams(query).toString(),
      },
    ]

    if (query) {
      requests.push(
        {
          indexName: indexes.collections,
          params: this.#linkParams({
            query,
            hitsPerPage: 4,
            attributesToRetrieve: LINK_ATTRIBUTES,
          }).toString(),
        },
        {
          indexName: indexes.pages,
          params: this.#linkParams({
            query,
            hitsPerPage: 4,
            attributesToRetrieve: LINK_ATTRIBUTES,
          }).toString(),
        }
      )
    }

    return requests
  }

  #linkParams({ query, hitsPerPage, attributesToRetrieve }) {
    const params = new URLSearchParams()
    params.set('query', query)
    params.set('hitsPerPage', String(hitsPerPage))
    params.set('clickAnalytics', 'true')
    params.set('ruleContexts', JSON.stringify([SEARCH_RULE_CONTEXT]))
    params.set('userToken', algoliaUserToken(this.#config))
    params.set('attributesToRetrieve', JSON.stringify(attributesToRetrieve))

    return params
  }

  #productParams(query) {
    const params = new URLSearchParams()
    params.set('query', query)
    params.set('hitsPerPage', this.#isDesktop() ? '3' : '4')
    params.set('distinct', 'true')
    params.set('facetingAfterDistinct', 'true')
    params.set('clickAnalytics', 'true')
    params.set('ruleContexts', JSON.stringify([SEARCH_RULE_CONTEXT]))
    params.set('userToken', algoliaUserToken(this.#config))
    params.set('filters', this.#config.algolia.defaultFilters)
    params.set(
      'attributesToRetrieve',
      JSON.stringify(algoliaProductAttributes(this.#config.market.handle))
    )
    params.set('attributesToHighlight', '[]')

    return params
  }

  #renderResults(query, results) {
    const [suggestionsResult, productsResult, collectionsResult, pagesResult] =
      results
    const suggestions = this.#filterSuggestions(
      query,
      suggestionsResult?.hits || []
    )
    const products = productsResult?.hits || []
    const collections = collectionsResult?.hits || []
    const pages = pagesResult?.hits || []
    const productCount = productsResult?.nbHits || 0
    const resultCount =
      productCount +
      (collectionsResult?.nbHits || 0) +
      (pagesResult?.nbHits || 0)

    this.#showContent(true)
    this.#renderSuggestionGroups({ query, suggestions, collections, pages })
    this.#renderProducts({
      indexName: this.#productIndex(query),
      productCount,
      products,
      query,
      queryID: productsResult?.queryID,
    })
    this.#setEmptyState(query, query && resultCount === 0)
  }

  #renderSuggestionGroups({ query, suggestions, collections, pages }) {
    const suggestionGroup = this.#renderGroup({
      label: query
        ? this.#config.strings.suggestions
        : this.#config.strings.popularSearches,
      items: suggestions,
      emptyLabel: this.#config.strings.noResults,
      getText: item => item.query || '',
      getHref: item => this.#searchUrl(item.query || ''),
      highlightAttribute: 'query',
    })
    const collectionGroup = query
      ? this.#renderGroup({
          label: this.#config.strings.collections,
          items: collections,
          emptyLabel: this.#config.strings.noResults,
          getText: item => item.title || '',
          getHref: item => this.#resourceUrl('collections', item.handle),
          highlightAttribute: 'title',
        })
      : ''
    const pageGroup = query
      ? this.#renderGroup({
          label: this.#config.strings.pages,
          items: pages,
          emptyLabel: this.#config.strings.noResults,
          getText: item => item.title || '',
          getHref: item => this.#resourceUrl('pages', item.handle),
          highlightAttribute: 'title',
        })
      : ''

    if (this.#isDesktop()) {
      this.#primaryGroups.innerHTML =
        [suggestionGroup, collectionGroup, pageGroup]
          .filter(Boolean)
          .join('<hr class="header-search-overlay__divider">') +
        this.#logoMarkup()
      this.#mobileGroups.innerHTML = ''
      return
    }

    // Mobile reads top to bottom: suggestions, collections, products, pages.
    this.#primaryGroups.innerHTML = [suggestionGroup, collectionGroup]
      .filter(Boolean)
      .join('<hr class="header-search-overlay__divider">')
    this.#mobileGroups.innerHTML = pageGroup
  }

  #filterSuggestions(query, hits) {
    const typed = this.#normaliseSuggestion(query)
    const seen = new Set()

    return hits
      .filter(hit => {
        const text = this.#normaliseSuggestion(hit.query)

        if (!text || text === typed || seen.has(text)) {
          return false
        }

        if (SUGGESTION_CODE_PATTERN.test(text)) {
          return false
        }

        seen.add(text)
        return true
      })
      .slice(0, typed ? SUGGESTION_LIMIT : POPULAR_SEARCH_LIMIT)
  }

  #normaliseSuggestion(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ')
  }

  #renderProducts({ indexName, query, products, productCount, queryID }) {
    this.#productsLabel.textContent = query
      ? this.#config.strings.products
      : this.#config.strings.featuredProducts
    this.#viewResults.hidden = !query || productCount <= 0
    this.#viewResults.href = this.#searchUrl(query)
    this.#viewResults.textContent = this.#config.strings.viewResults.replace(
      '__count__',
      String(productCount)
    )

    if (!products.length) {
      this.#swatchRequestId += 1
      this.#products.innerHTML = `<p class="text-body-xs">${escapeHtml(this.#config.strings.noResults)}</p>`
      return
    }

    const formattedProducts = products.map((product, index) =>
      this.#fetcher.formatProduct(product, {
        indexName,
        position: index + 1,
        queryID,
      })
    )

    this.#products.innerHTML = formattedProducts
      .map((product, index) => this.#cardRenderer.render(product, index))
      .join('')
    void this.#hydrateProductSwatches(formattedProducts)
  }

  async #hydrateProductSwatches(products) {
    const requestId = ++this.#swatchRequestId
    const cards = [...this.#products.querySelectorAll('[data-product-card]')]
    const familyTags = products
      .map(product => this.#fetcher.familyTag(product))
      .filter(Boolean)

    if (!familyTags.length) {
      return
    }

    const relatedProductsByTag =
      await this.#fetcher.fetchRelatedProductsBatch(familyTags)

    if (requestId !== this.#swatchRequestId) {
      return
    }

    cards.forEach((card, index) => {
      const product = products[index]
      const familyTag = product ? this.#fetcher.familyTag(product) : ''
      const relatedProducts = relatedProductsByTag.get(familyTag) || []

      if (!product || !familyTag || !card.isConnected) {
        return
      }

      this.#cardRenderer.renderSwatches(
        card,
        product,
        this.#fetcher.filterColourProducts(product, relatedProducts)
      )
    })
  }

  #selectSwatch(swatch) {
    const card = swatch.closest('[data-product-card]')
    const product = parseJson(swatch.dataset.product)
    const currentProduct = this.#cardProduct(card)

    if (!card || !product) {
      return
    }

    if (product.handle === currentProduct?.handle) {
      return
    }

    this.#replaceCard(card, {
      ...product,
      __indexName: currentProduct?.__indexName || product.__indexName,
      __position: currentProduct?.__position || product.__position,
      __queryID: currentProduct?.__queryID || product.__queryID,
    })
  }

  #replaceCard(card, product) {
    const cards = [...this.#products.querySelectorAll('[data-product-card]')]
    const index = Math.max(cards.indexOf(card), 0)
    const template = document.createElement('template')

    template.innerHTML = this.#cardRenderer.render(product, index).trim()

    const newCard = template.content.firstElementChild

    if (!newCard) {
      return
    }

    newCard.className = card.className
    card.replaceWith(newCard)
    void this.#hydrateCardSwatches(newCard, product)
  }

  #productIndex(query = this.#query) {
    return query.trim()
      ? this.#config.algolia.indexes.products
      : this.#config.algolia.indexes.trendingProducts
  }

  async #hydrateCardSwatches(card, product) {
    const familyTag = product ? this.#fetcher.familyTag(product) : ''

    if (!familyTag) {
      return
    }

    const relatedProducts =
      (await this.#fetcher.fetchRelatedProductsBatch([familyTag])).get(
        familyTag
      ) || []

    if (!card.isConnected) {
      return
    }

    this.#cardRenderer.renderSwatches(
      card,
      product,
      this.#fetcher.filterColourProducts(product, relatedProducts)
    )
  }

  #renderGroup({
    label,
    items,
    emptyLabel,
    getText,
    getHref,
    highlightAttribute,
  }) {
    const list = items.length
      ? `
        <ul class="header-search-overlay__list">
          ${items
            .map(item => {
              const text = getText(item)
              const href = getHref(item)

              return `
                <li>
                  <a class="header-search-overlay__link text-body-md" href="${escapeHtml(href)}">
                    ${this.#highlight(item, highlightAttribute, text)}
                  </a>
                </li>
              `
            })
            .join('')}
        </ul>
      `
      : `<p class="text-body-xs">${escapeHtml(emptyLabel)}</p>`

    return `
      <div class="header-search-overlay__group">
        <p class="header-search-overlay__group-label text-body-sm">${escapeHtml(label)}</p>
        ${list}
      </div>
    `
  }

  #renderEmpty(query) {
    this.#showContent(false)
    this.#setEmptyState(query, true)
  }

  #showContent(showContent) {
    this.querySelector('[data-header-search-content]').hidden = !showContent
    this.#empty.hidden = true
  }

  #setEmptyState(query, isVisible) {
    this.#empty.hidden = !isVisible
    const title = query
      ? this.#config.strings.noResultsFor.replace('__query__', query)
      : this.#config.strings.noResults

    this.#emptyTitle.textContent = this.#decodeHtml(title)
  }

  #setLoading(isLoading) {
    if (!this.#status) {
      return
    }

    this.#status.hidden = !isLoading
  }

  #cardProduct(card) {
    return parseJson(card?.querySelector('[data-card-product]')?.textContent)
  }

  #highlight(item, attribute, fallback) {
    const highlighted = item._highlightResult?.[attribute]?.value

    if (!highlighted) {
      return escapeHtml(fallback)
    }

    return escapeHtml(highlighted)
      .replace(/&lt;em&gt;/g, '<b>')
      .replace(/&lt;\/em&gt;/g, '</b>')
  }

  #decodeHtml(value) {
    const textarea = document.createElement('textarea')
    textarea.innerHTML = value
    return textarea.value
  }

  #logoMarkup() {
    const eagle = window.icons?.eagle || ''

    if (!eagle) {
      return ''
    }

    return `
      <a class="header-search-overlay__logo" href="${escapeHtml(getLocalisedRoute(this.#config.routes.root))}" aria-label="${escapeHtml(this.#config.strings.returnHome)}">
        ${eagle}
      </a>
    `
  }

  #syncInputs(value, source = null) {
    this.#inputs.forEach(input => {
      if (input !== source) {
        input.value = value
      }
    })
    this.#syncClearButtons()
  }

  #syncClearButtons() {
    ;[
      ...new Set([
        ...(this.#header?.querySelectorAll('[data-header-search-clear]') || []),
        ...this.querySelectorAll('[data-header-search-clear]'),
      ]),
    ].forEach(clear => {
      clear.hidden = !this.#isOpen
    })
  }

  #focusActiveInput() {
    if (this.#isDesktop()) {
      this.#focusDesktopInput()
      return
    }

    this.querySelector('[data-header-search-input]')?.focus()
  }

  #focusDesktopInput() {
    this.#inputs
      .find(candidate =>
        candidate.closest("[data-header-search-view='desktop']")
      )
      ?.focus()
  }

  #resourceUrl(type, handle) {
    if (!handle) {
      return getLocalisedRoute(this.#config.routes.root)
    }

    return getLocalisedRoute(`${this.#config.market.rootUrl}${type}/${handle}`)
  }

  #searchUrl(query) {
    const params = new URLSearchParams()
    params.set('query', query)

    return `${getLocalisedRoute(this.#config.routes.search)}?${params.toString()}`
  }

  #hasAlgoliaConfig() {
    return Boolean(
      this.#config.algolia.appId &&
      this.#config.algolia.apiKey &&
      this.#config.algolia.indexes.products
    )
  }

  #isDesktop() {
    return window.matchMedia(DESKTOP_BREAKPOINT).matches
  }

  #parseConfig() {
    return parseJson(
      this.querySelector('[data-header-search-config]')?.textContent
    )
  }
}

if (!customElements.get('lyle-header-search')) {
  customElements.define('lyle-header-search', LyleHeaderSearch)
}
